/**
 * Type Definitions for ORCA360 CBTA Trainee Sim Assessment Export
 *
 * These types define the structure for exporting the pilot assessment
 * to PDF and Word formats, following the ORCA360 CBTA template.
 * 
 * Template Structure:
 * - Part A: Session Identification
 * - Part B: Threat & Error Management (TEM) Overview
 * - Part C: Competency Assessment (9 competencies)
 * - Part D: Root Cause Analysis & Training Recommendations
 */

import {
  captureRecordFromRow,
  GRADE_LABEL_BY_VALUE,
  PILOT_COMPETENCY_CODES,
  PILOT_COMPETENCY_FULL_NAMES,
  COMPETENCY_ORDER as SHARED_COMPETENCY_ORDER,
  resolveFocusRows,
  type CaptureRecord,
  type FrameworkCompetencyCode,
  type PilotCompetencyCode,
  type ServerFocusEntry,
} from '@orca360/shared';
import type { DebriefingPartAData } from './debriefing-export';

// =====================================================================
// COMPETENCY DEFINITIONS (single source: shared framework module)
// =====================================================================

/** The nine reference-model codes. Membership tests only, never a payload type. */
export const PILOT_COMPETENCIES: readonly PilotCompetencyCode[] = PILOT_COMPETENCY_CODES;

/**
 * Competency code an export payload may carry (HG-3B G3-04.3).
 *
 * Sourced from the shared framework module, and deliberately the OPR-inclusive
 * type rather than the nine reference codes: a tenant framework version with an
 * Operator Competency puts OPR last in its listing order, and until this
 * widened, mapAssessmentsToPartC could not be handed such an order at all. That
 * was the gap the HG-2A summary flagged, and the note on mapAssessmentsToPartC
 * below is withdrawn accordingly.
 */
export type PilotCompetency = FrameworkCompetencyCode;

/**
 * Long-form display labels. Keyed by string, not by PilotCompetency: the map is
 * the reference model's and holds no Operator Competency name, because an
 * operator's own label lives in its registry version and is read at run time.
 */
export const COMPETENCY_FULL_NAMES: Record<string, string> =
  PILOT_COMPETENCY_FULL_NAMES;

/**
 * Phase 5d — canonical render order per WingMentor CBTA/EBT Core
 * Framework 2.0 and Andre's Section 7 model. Used by mapAssessmentsToPartC
 * to order Part C rows in PDF / DOCX / online views.
 * Single source: shared framework module (FRAMEWORK_LISTING_ORDER_V1).
 */
export const COMPETENCY_ORDER: readonly PilotCompetency[] = SHARED_COMPETENCY_ORDER;

// =====================================================================
// GRADE DEFINITIONS
// =====================================================================

/** Grade labels come from the single canonical source; never redefine them here. */
export const GRADE_LABELS: Record<number, string> = GRADE_LABEL_BY_VALUE;

// =====================================================================
// Part A: Session Identification (reuse from debriefing-export)
// =====================================================================

// Re-export DebriefingPartAData as PilotAssessmentPartA for clarity
export type PilotAssessmentPartA = DebriefingPartAData;

// =====================================================================
// Part B: TEM Overview
// =====================================================================

/** Threat category types matching ORCA360 TEM framework */
export type ThreatCategory =
  | 'Environmental'
  | 'Organizational'
  | 'Aircraft'
  | 'Airport'
  | 'ATC'
  | 'Weather'
  | 'Terrain'
  | 'Other'
  | 'Environmental/Operational'; // Legacy fallback

/** Threat management status */
export type ThreatManagement = 'Managed' | 'Mismanaged' | 'Unmanaged';

export interface TEMThreat {
  /** Threat category (Environmental, Organizational, Aircraft, etc.) */
  category: string;
  /** Description of the threat */
  description?: string;
  /** Primary competency affected by this threat */
  primaryCompetency: PilotCompetency;
  /** Secondary competencies affected */
  secondaryCompetencies: PilotCompetency[];
  /** How the threat was managed */
  management?: ThreatManagement;
  /** Evidence from transcript */
  evidence?: string;
}

/** Error type classification */
export type ErrorType = 'Procedural' | 'Communication' | 'Proficiency' | 'Operational Decision' | 'Other';

/** Error classification */
export type ErrorClassification = 'Commission' | 'Omission';

export interface TEMError {
  /** Type of error */
  type: ErrorType;
  /** Classification (commission or omission) */
  classification?: ErrorClassification;
  /** Description of the error */
  description: string;
  /** Whether the error led to UAS */
  ledToUAS?: boolean;
  /** Evidence from transcript */
  evidence?: string;
}

export interface TEMResilienceFactor {
  /** Description of the resilience factor */
  description: string;
  /** Competencies that contributed to resilience */
  competencies?: PilotCompetency[];
  /** Evidence from transcript */
  evidence?: string;
}

export interface TEMUAS {
  /** Description of the Undesired Aircraft State */
  description: string;
  /** Severity: Minor, Moderate, Serious */
  severity?: 'Minor' | 'Moderate' | 'Serious';
  /** How the crew recovered */
  recovery?: string;
  /** Evidence from transcript */
  evidence?: string;
}

export interface PilotAssessmentPartB {
  /** Brief description of operational context and TEM management */
  temOverview: string;
  /** Threats identified during session */
  threats: TEMThreat[];
  /** Errors identified during session (structured or string fallback) */
  errors: (TEMError | string)[];
  /** Resilience factors demonstrated (structured or string fallback) */
  resilienceFactors: (TEMResilienceFactor | string)[];
  /** Undesired Aircraft State events */
  uasEvents: TEMUAS[];
}

// =====================================================================
// ORCA360 V2 Part B Database Format (from orca360_assessments.part_b)
// =====================================================================

/** Database format for Part B from orca360_assessments table */
export interface ORCA360PartBDatabase {
  operationalContext?: string;
  threats?: Array<{
    category: string;
    description: string;
    primaryCompetency: string;
    secondaryCompetencies?: string[];
    management?: string;
    evidence?: string;
  }>;
  errors?: Array<{
    type?: string;
    classification?: string;
    description: string;
    ledToUAS?: boolean;
    evidence?: string;
  }>;
  resilienceFactors?: Array<{
    description: string;
    competencies?: string[];
    evidence?: string;
  }>;
  uasEvents?: Array<{
    description: string;
    severity?: string;
    recovery?: string;
    evidence?: string;
  }>;
  managementSummary?: string;
}

// =====================================================================
// Part C: Competency Assessment
// =====================================================================

export interface ObservableBehavior {
  /** OB code from WingMentor Core Framework, e.g., "COM-3", "KNO-7" */
  code: string;
  /** Short description of the behavior */
  description: string;
}

export interface EvidenceItem {
  /** Direct quote from transcript */
  quote: string;
  /** Timestamp of the evidence, e.g., "14:23:05" */
  timestamp: string;
  /** Validation status from anti-hallucination system */
  validation_status: 'VERIFIED' | 'PARAPHRASED' | 'SIMILAR' | 'UNVERIFIED' | 'VERBATIM';
  /** Confidence score (0-1) */
  confidence?: number;
  /** LLM validation decision (Tier 4) */
  llm_decision?: 'ACCEPTED' | 'REJECTED' | 'RECLASSIFIED';
  /** LLM reasoning for decision */
  llm_reasoning?: string;
  /** Observable Behavior code, e.g., "COM-3", "KNO-7" - LLM-extracted */
  obCode?: string;
  /** Source of the OB code: 'llm' (validated against framework) or 'fallback' (sentinel code) */
  obCodeSource?: 'llm' | 'fallback';
  /** LLM analytical comment for this evidence item. Negative-tone for sub-3
   *  grades, neutral/positive otherwise. Preferred over the OB description in
   *  Part C overview cells so the rendered text faithfully reflects the score. */
  observation?: string;
}

export interface CompetencyAssessment {
  /** Competency code (PRO, COM, etc.) */
  competency: PilotCompetency;
  /** Grade 1-5 */
  score: number;
  /** Observable behaviors demonstrated */
  observableBehaviors: ObservableBehavior[];
  /** Evidence items with quotes and timestamps */
  evidence: EvidenceItem[];
  /** Instructor comments linked to OBs */
  instructorComments: string;
  /**
   * The Step 6 capture record: the three dimensions, the limiting factor, the
   * override record and the per-competency development level (GCH-14, ICD 3.1.2
   * and 3.1.4). Undefined on a row that carries none, which the generators
   * render as not recorded and never as zero (GCH-35).
   */
  capture?: CaptureRecord;
}

export interface PilotAssessmentPartC {
  /** All 9 competency assessments */
  competencyAssessments: CompetencyAssessment[];
}

// =====================================================================
// Part D: Root Cause Analysis & Training Recommendations
// =====================================================================

export interface FocusCompetencyAnalysis {
  /** The competency requiring focus */
  competency: PilotCompetency;
  /** Competency full name */
  competencyName: string;
  /** Why did it happen? (Causal Factor) */
  causalFactor: string;
  /** Remedial Training / Recommendations */
  recommendations: string;
}

export interface PilotAssessmentPartD {
  /** Focus competencies requiring RCA (typically 2-4) */
  focusCompetencies: FocusCompetencyAnalysis[];
}

// =====================================================================
// COMPLETE PILOT ASSESSMENT EXPORT DATA
// =====================================================================

export interface PilotAssessmentExportData {
  partA: PilotAssessmentPartA;
  partB: PilotAssessmentPartB;
  partC: PilotAssessmentPartC;
  partD: PilotAssessmentPartD;
  /** Invocation line at end of report */
  invocationLine?: string;
  /** Metadata */
  metadata?: {
    generatedAt: string;
    version: string;
    traineeId: string;
    pilotRole?: 'captain' | 'captain_2' | 'first_officer' | 'first_officer_2';
    /**
     * The GCH-32 framework stamp carried on the assessment rows (migration
     * 234). Preferred over the framework resolved at export time, because this
     * is the version the grades were recorded against; the resolved framework
     * is only the fallback when a payload carries no stamp.
     */
    frameworkId?: string;
    frameworkVersion?: string;
  };
}

// =====================================================================
// DATABASE TO EXPORT MAPPING
// =====================================================================

export interface AssessmentPilotRow {
  id: string;
  session_id: string;
  trainee_id: string | null;
  org_pilot_id?: string | null;
  pilot_role?: 'captain' | 'captain_2' | 'first_officer' | 'first_officer_2';
  competency: string;
  score: number;
  evidence: EvidenceItem[];
  /** Competency-level OB aggregate (migration 203). Authoritative source for
   * the Part C "Observable Behaviours" column. Empty array means NA (score
   * IS NULL) per the Phase 4 contract. Legacy rows (pre-migration) have
   * undefined here — the renderer falls back to dedupping per-item obCode. */
  observable_behaviors?: string[];
  analysis: string; // 5-Whys / RCA
  goals: string; // SMART goals / recommendations
  facilitator_questions?: string[];
  manual_observations?: string;
  status: 'draft' | 'published';
  version: number;
  ai_generated: boolean;
  created_at: string;
  updated_at: string;
  approved_at?: string;
  approved_by?: string;
  /** GCH-32 framework stamp (migration 234). The version this grade was recorded against. */
  framework_id?: string | null;
  framework_version?: string | null;
  /** ICD 9.3 historical marker (migration 248). */
  historical?: boolean | null;
  /** The three dimensions and the limiting factor (migration 248, GCH-11, GCH-12). */
  how_many?: string | null;
  how_often?: string | null;
  tem_outcome?: string | null;
  limiting_factor?: string | null;
  /** The override record (migration 248, GCH-13, DEC-28). */
  proposed_grade?: number | null;
  override_flag?: boolean | null;
  override_category?: string | null;
  override_note?: string | null;
  override_actor?: string | null;
  override_at?: string | null;
  /** Per-competency development level with source (migration 248, ICD 3.1.4). */
  development_level?: string | null;
  development_level_source?: string | null;
  development_level_assigned_at?: string | null;
}

/**
 * Lift the capture columns off a stored row into the payload shape.
 *
 * Copies, never derives. In particular it does not recompute proposed_grade
 * from the dimensions and does not infer the limiting factor: ICD 8.7 makes
 * both stored judgments that a recomputation must not overwrite, and an export
 * is the last place that rule should be broken.
 */
export function captureFromRow(row: AssessmentPilotRow): CaptureRecord {
  // HG-3C G3-11: one mapping, shared with Step 8 and with mobile ReportStep.
  // Three hand-written copies of the same field list is how the three surfaces
  // would eventually disagree about what an absent value looks like.
  return captureRecordFromRow(row);
}

export interface SessionForExport {
  id: string;
  session_date: string;
  aircraft_type: string;
  simulator_id?: string;
  scenario_code?: string;
  flight_phase?: string;
  session_notes?: string;
  session_start_time?: string;
  session_end_time?: string;
  time_pf_minutes?: number;
  time_pm_minutes?: number;
  location?: string;
  overall_result?: 'Pass' | 'Fail' | 'Incomplete';
  pilot_function?: 'Captain' | 'First Officer' | 'Instructor Trainee' | 'Other';
  trainee?: {
    id: string;
    full_name: string;
    licence_id?: string;
  };
  instructor?: {
    id: string;
    full_name: string;
    licence_id?: string;
  };
  organization?: {
    id: string;
    name: string;
  };
}

// =====================================================================
// EXPORT CONFIGURATION
// =====================================================================

export interface PilotAssessmentExportOptions {
  format: 'pdf' | 'docx';
  includeLogo?: boolean;
  includeHeaders?: boolean;
  includeFooters?: boolean;
  includePageNumbers?: boolean;
  includePartA?: boolean;
  includePartB?: boolean;
  includePartC?: boolean;
  includePartD?: boolean;
  includeSignatures?: boolean;
}

// =====================================================================
// HELPER FUNCTIONS
// =====================================================================

/**
 * One export is ONE pilot's document, so every mapper below assumes it was
 * handed a single crew member's rows.
 *
 * That assumption used to be silent, and it failed silently: mapAssessmentsToPartC
 * resolves each competency with `.find()`, so a payload carrying both crew
 * members still produced exactly one row per competency and was structurally
 * indistinguishable from a correct one. The rows load ordered by pilot_role, so
 * the lexically first role (captain before first_officer) won every competency
 * and the other pilot's assessment vanished from a document that carried their
 * name and a signature block.
 *
 * The check is on DISTINCT NON-NULL pilot_role, deliberately, not on duplicate
 * competencies: filterAssessmentsForPilotRole keeps legacy rows whose
 * pilot_role is null alongside the selected role, and a duplicate-competency
 * test would reject those sessions for a condition that is not this defect.
 */
export class MultiPilotExportPayloadError extends Error {
  constructor(roles: string[]) {
    super(
      `ERR_MULTI_PILOT_EXPORT_PAYLOAD: export mappers take one pilot's rows, ` +
        `received ${roles.length} (${roles.join(', ')}). Scope the rows to the ` +
        `selected crew member (filterAssessmentsForPilotRole) before mapping.`,
    );
    this.name = 'MultiPilotExportPayloadError';
  }
}

function assertSinglePilot(assessments: AssessmentPilotRow[]): void {
  // pilot_role is a literal union, not a bare string, so the null/undefined
  // filter needs its own predicate to narrow it.
  const roles = [
    ...new Set(
      assessments
        .map((a) => a.pilot_role)
        .filter((r): r is NonNullable<typeof r> => r != null),
    ),
  ];
  if (roles.length > 1) throw new MultiPilotExportPayloadError([...roles].sort());
}

/**
 * Map database assessment rows to Part C structure
 */
export function mapAssessmentsToPartC(
  assessments: AssessmentPilotRow[],
  /**
   * Framework Listing Order to emit rows in (G2-02.1, GAT-02). Defaults to the
   * reference order. An operator order carrying OPR is now accepted: G3-04.3
   * widened PilotCompetency to the OPR-inclusive framework type, which was the
   * blocker the HG-2A summary flagged.
   */
  competencyOrder: readonly PilotCompetency[] = COMPETENCY_ORDER,
): PilotAssessmentPartC {
  assertSinglePilot(assessments);
  const competencyAssessments: CompetencyAssessment[] = competencyOrder.map(comp => {
    const assessment = assessments.find(a => a.competency === comp);
    
    if (!assessment) {
      return {
        competency: comp,
        score: 0,
        observableBehaviors: [],
        evidence: [],
        instructorComments: 'Not assessed',
      };
    }


    const evidence = assessment.evidence || [];
    // Phase 4 — prefer the authoritative competency-level aggregate from the
    // observable_behaviors column (migration 203). Falls back to dedupping
    // per-item evidence.obCode for legacy rows that pre-date the column.
    const aggregate = assessment.observable_behaviors;
    const codes = (aggregate && aggregate.length > 0)
      ? aggregate
      : Array.from(new Set(evidence.map((e) => e.obCode).filter((c): c is string => !!c)));
    const observableBehaviors: ObservableBehavior[] = codes.map((code) => ({ code, description: '' }));

    return {
      competency: comp,
      score: assessment.score || 0,
      observableBehaviors,
      evidence,
      instructorComments: assessment.manual_observations || assessment.analysis || '',
      capture: captureFromRow(assessment),
    };
  });

  return { competencyAssessments };
}

/**
 * Map database assessment rows to Part D structure (focus competencies)
 *
 * G3-06: `serverFocusList` is the list migration 258 computes from these same
 * live rows. Passed, it decides membership and order and is rendered verbatim,
 * so a grade 3 carrying deficiency evidence reaches the report. Omitted or
 * null, the GAT-04 client fallback selects grades 1 and 2 only.
 */
export function mapAssessmentsToPartD(
  assessments: AssessmentPilotRow[],
  serverFocusList?: ServerFocusEntry[] | null,
): PilotAssessmentPartD {
  // Part D fails differently from Part C on a multi-pilot payload: resolveFocusRows
  // keys on competency::pilot_role, so it emits BOTH pilots' focus lists interleaved
  // by focus_rank rather than shadowing one. Same cause, same refusal.
  assertSinglePilot(assessments);
  const focusCompetencies: FocusCompetencyAnalysis[] = resolveFocusRows(
    serverFocusList,
    assessments,
    { sortWorstFirst: true },
  ).items
    .map(a => ({
      competency: a.competency as PilotCompetency,
      competencyName: COMPETENCY_FULL_NAMES[a.competency as PilotCompetency] || a.competency,
      causalFactor: a.analysis || 'Analysis not available',
      recommendations: a.goals || 'No specific recommendations',
    }));

  return { focusCompetencies };
}

/**
 * Extract TEM information from assessments for Part B
 */
export function extractTEMFromAssessments(assessments: AssessmentPilotRow[]): PilotAssessmentPartB {
  const threats: TEMThreat[] = [];
  const errors: string[] = [];
  const resilienceFactors: string[] = [];

  // Analyze evidence and analysis text for TEM indicators
  for (const assessment of assessments) {
    const text = `${assessment.analysis || ''} ${assessment.manual_observations || ''}`.toLowerCase();
    
    // Check for threat indicators
    if (text.includes('threat') || text.includes('challenge') || text.includes('complexity')) {
      threats.push({
        category: 'Environmental/Operational',
        primaryCompetency: assessment.competency as PilotCompetency,
        secondaryCompetencies: [],
      });
    }

    // Check for error indicators
    if (text.includes('error') || text.includes('deviation') || text.includes('missed')) {
      const errorMatch = text.match(/(?:error|deviation|missed)[^.]*\./i);
      if (errorMatch) {
        errors.push(errorMatch[0]);
      }
    }

    // Check for resilience indicators
    if (text.includes('recovery') || text.includes('resilience') || text.includes('corrected') || assessment.score >= 4) {
      resilienceFactors.push(`${COMPETENCY_FULL_NAMES[assessment.competency as PilotCompetency]}: Demonstrated proficiency`);
    }
  }

  return {
    temOverview: 'TEM analysis based on session transcript and competency assessment.',
    threats: threats.slice(0, 3),
    errors: errors.slice(0, 5),
    resilienceFactors: resilienceFactors.slice(0, 5),
    uasEvents: [],
  };
}

/**
 * Create complete export data from database records
 */
export function createPilotAssessmentExportData(
  session: SessionForExport,
  assessments: AssessmentPilotRow[],
  partA: PilotAssessmentPartA,
  serverFocusList?: ServerFocusEntry[] | null,
): PilotAssessmentExportData {
  return {
    partA,
    partB: extractTEMFromAssessments(assessments),
    partC: mapAssessmentsToPartC(assessments),
    partD: mapAssessmentsToPartD(assessments, serverFocusList),
    invocationLine: 'Please verify the assessment and inform the company.',
    metadata: {
      generatedAt: new Date().toISOString(),
      version: '1.0',
      traineeId: session.trainee?.id || '',
      ...frameworkStampFromRows(assessments),
    },
  };
}

/**
 * The framework stamp to put on an export, taken from the assessment rows.
 *
 * Every row of one session carries the same stamp (migration 234 backfilled and
 * then made both columns NOT NULL), so the first row that has one is the
 * session's. Returns an empty object rather than a placeholder when no row
 * carries one, so the generator falls back to the framework it resolved instead
 * of printing an invented version.
 */
export function frameworkStampFromRows(
  assessments: AssessmentPilotRow[],
): { frameworkId?: string; frameworkVersion?: string } {
  const stamped = assessments.find(a => a.framework_version);
  if (!stamped?.framework_version) return {};
  return {
    frameworkId: stamped.framework_id ?? undefined,
    frameworkVersion: stamped.framework_version,
  };
}

/**
 * Map ORCA360 V2 Part B data from database to export format
 *
 * This function converts the AI-generated TEM analysis from the
 * orca360_assessments.part_b JSONB column to the export format.
 *
 * @param partBData - The part_b JSONB data from orca360_assessments table
 * @returns PilotAssessmentPartB formatted for PDF/Word export
 */
export function mapORCA360PartBToExport(partBData: ORCA360PartBDatabase | null): PilotAssessmentPartB {
  if (!partBData) {
    return {
      temOverview: 'TEM analysis not available.',
      threats: [],
      errors: [],
      resilienceFactors: [],
      uasEvents: [],
    };
  }

  // Map threats with competency validation
  const threats: TEMThreat[] = (partBData.threats || []).map(threat => {
    // Validate and map primary competency
    const primaryComp = (PILOT_COMPETENCIES as readonly string[]).includes(threat.primaryCompetency)
      ? (threat.primaryCompetency as PilotCompetency)
      : 'SAW'; // Default to SAW if invalid

    // Validate and map secondary competencies
    const secondaryComps = (threat.secondaryCompetencies || [])
      .filter(c => (PILOT_COMPETENCIES as readonly string[]).includes(c))
      .map(c => c as PilotCompetency);

    return {
      category: threat.category || 'Other',
      description: threat.description || '',
      primaryCompetency: primaryComp,
      secondaryCompetencies: secondaryComps,
      management: (threat.management as ThreatManagement) || undefined,
      evidence: threat.evidence,
    };
  });

  // Map errors - keep structure if available
  const errors: (TEMError | string)[] = (partBData.errors || []).map(error => {
    if (typeof error === 'string') return error;
    return {
      type: (error.type as ErrorType) || 'Other',
      classification: (error.classification as ErrorClassification) || undefined,
      description: error.description || '',
      ledToUAS: error.ledToUAS,
      evidence: error.evidence,
    };
  });

  // Map resilience factors - keep structure if available
  const resilienceFactors: (TEMResilienceFactor | string)[] = (partBData.resilienceFactors || []).map(rf => {
    if (typeof rf === 'string') return rf;
    return {
      description: rf.description || '',
      competencies: (rf.competencies || [])
        .filter(c => (PILOT_COMPETENCIES as readonly string[]).includes(c))
        .map(c => c as PilotCompetency),
      evidence: rf.evidence,
    };
  });

  // Map UAS events
  const uasEvents: TEMUAS[] = (partBData.uasEvents || []).map(u => ({
    description: u.description || '',
    severity: (u.severity as TEMUAS['severity']) || undefined,
    recovery: u.recovery,
    evidence: u.evidence,
  }));

  return {
    temOverview: partBData.operationalContext || partBData.managementSummary || 'TEM analysis based on session transcript.',
    threats,
    errors,
    resilienceFactors,
    uasEvents,
  };
}
